import axios from 'axios';
import { SavedLocation, SavedLocationCreate, SavedLocationUpdate } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) {
      // Handle unauthorized error (user not logged in)
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
  }
  throw error;
};

// Get all saved locations
export const getSavedLocations = async (): Promise<SavedLocation[]> => {
  try {
    console.log('Getting saved locations from API');
    const response = await axios.get(`${API_BASE_URL}/saved-locations`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error getting saved locations:', error);
    return handleApiError(error);
  }
};

// Get a saved location by ID
export const getSavedLocation = async (id: string): Promise<SavedLocation> => {
  try {
    console.log('Getting saved location with id:', id);
    const response = await axios.get(`${API_BASE_URL}/saved-locations/${id}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error getting saved location:', error);
    return handleApiError(error);
  }
};

// Create a new saved location
export const createSavedLocation = async (location: SavedLocationCreate): Promise<SavedLocation> => {
  try {
    console.log('Creating saved location with data:', location);
    const locationData = {
      ...location,
      time: location.time || '12:00'
    };
    
    const response = await axios.post(`${API_BASE_URL}/saved-locations`, locationData, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating saved location:', error);
    return handleApiError(error);
  }
};

// Update a saved location
export const updateSavedLocation = async (id: string, location: SavedLocationUpdate): Promise<SavedLocation> => {
  try {
    console.log('Updating saved location with id:', id, 'and data:', location);
    const response = await axios.put(`${API_BASE_URL}/saved-locations/${id}`, location, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating saved location:', error);
    return handleApiError(error);
  }
};

// Delete a saved location
export const deleteSavedLocation = async (id: string): Promise<void> => {
  try {
    console.log('Deleting saved location with id:', id);
    await axios.delete(`${API_BASE_URL}/saved-locations/${id}`, {
      headers: getAuthHeaders()
    });
  } catch (error) {
    console.error('Error deleting saved location:', error);
    handleApiError(error);
  }
};

// Reorder saved locations
export const reorderSavedLocations = async (reorderedLocations: SavedLocation[]): Promise<SavedLocation[]> => {
  try {
    console.log('Reordering saved locations:', reorderedLocations);
    const locationIds = reorderedLocations.map(loc => loc.id);
    const response = await axios.post(`${API_BASE_URL}/saved-locations/reorder`, {
      location_ids: locationIds
    }, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error reordering saved locations:', error);
    return handleApiError(error);
  }
}; 